'use client';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { endpoints, http, logger } from '@/config';
import { useUser } from '@/context/userContext';
import useToggle from '@/hooks/useToggle';
import { useCleverTap } from './clevertapContext';
import { pushGA4Event } from '@/utils/helper';

interface PropTypes {
  children: ReactNode;
}

export interface WishlistItemPropTypes {
  id: string;
  in_stock: boolean;
  time_to_cooking: string;
  author: string;
  url_key: string;
  product_id: string;
  options: any[];
  product_name: string;
  weight: number;
  product_attribute_set: string;
  product_sku: string;
  product_image: string;
  product_price: number;
  product_special_price: number;
}

type ItemTypes = {
  sku: string;
  name: string;
  price?: number;
};

interface useWishlistPropTypes {
  addToWishlist: (item: ItemTypes, cb?: () => void) => Promise<boolean>;
  removeFromWishlist: (item: ItemTypes) => void;
  isSkuInWishlist: (item: ItemTypes) => boolean;
  getWishlistItems: () => Promise<{ data: any; response: any } | undefined>;
  wishlistItems: WishlistItemPropTypes[];
  isLoading: boolean;
  animateHeart: boolean;
  wishlistToast?: {
    message: string;
    type: 'success' | 'failure';
  };
}

const Context = createContext<any | null>(null);

const WishlistProvider: FC<PropTypes> = ({ children }) => {
  const { userDetails, setLoginModalMessage, setShowLoginModal } = useUser();
  const { value: isLoading, toggleValue: setLoading } = useToggle(false);
  const [wishlistItems, setWishlistItems] = useState<WishlistItemPropTypes[]>(
    []
  );
  const [wishlistToast, setWishlistToast] = useState<
    { message: string; type: 'success' | 'failure' } | undefined
  >();
  const [animateHeart, setAnimateHeart] = useState(false);
  const { pushEvent } = useCleverTap();

  const getWishlistItems = async () => {
    try {
      setLoading(true);
      const { data, response } = await http.get(endpoints.WISHLIST);
      if (response.ok) {
        setWishlistItems(data);
        return { data, response };
      } else throw new Error(data?.message);
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setLoading(false);
    }
  };

  const findSkuInWishlist = (
    sku: string,
    returnValue: 'boolean' | 'wishlistId'
  ) => {
    const matchingItem = wishlistItems.find(
      item =>
        item.product_sku?.trim().toLowerCase() === sku?.trim().toLowerCase()
    );
    if (returnValue === 'boolean') {
      return !!matchingItem;
    }
    if (matchingItem && matchingItem?.id) {
      return matchingItem.id;
    }
    return null;
  };

  const isSkuInWishlist = (item: ItemTypes) => {
    const itemFound = findSkuInWishlist(item?.sku, 'boolean');
    if (itemFound) {
      return true;
    }
    return false;
  };

  const addToWishlist: useWishlistPropTypes['addToWishlist'] = useCallback(
    async (item: ItemTypes, cb?: () => void) => {
      if (userDetails?.firstname) {
        setLoading(true);
        const res = await http.put(`${endpoints.WISHLIST}/${item?.sku}`);
        if (res?.response.ok) {
          pushEvent({
            event: 'Wishlist updated',
            data: {
              'Action Type': 'Add',
              'Product ID': item?.sku,
              'Product Name': item?.name,
              'Product Price': item?.price ?? 0,
              'Wishlist Modification Date': new Date().toLocaleDateString(),
            },
          });
          pushGA4Event('wishlist_add', {
            category: 'Wishlist',
            item_id: item.sku,
          });
          await getWishlistItems();
          setLoading(false);
          setAnimateHeart(true);
          setWishlistToast({
            message: `"${item?.name}" added to the shelf`,
            type: 'success',
          });
          setTimeout(() => {
            setAnimateHeart(false);
            setWishlistToast(undefined);
          }, 2000);
          // toast.success(`"${item?.name}" added to the shelf`);
          return Promise.resolve(true);
        } else {
          toast.error(res?.data?.message);
          return Promise.resolve(false);
        }
      } else {
        setLoginModalMessage(
          'Login using your mobile number to save your favourite food stories'
        );
        setShowLoginModal(true);
        if (cb) {
          cb();
        }
        return Promise.resolve(false);
      }
    },
    [userDetails]
  );

  const removeFromWishlist: useWishlistPropTypes['removeFromWishlist'] = async (
    item: ItemTypes
  ) => {
    setLoading(true);
    const wishlistItemId = findSkuInWishlist(item.sku, 'wishlistId');
    if (wishlistItemId !== null) {
      try {
        // First make the API call
        const res = await http.delete(
          `${endpoints.WISHLIST}/${wishlistItemId}`
        );
        if (res?.response.ok) {
          // Update the wishlistItems state after successful API call
          setWishlistItems(prevItems =>
            prevItems.filter(wishlistItem => wishlistItem.id !== wishlistItemId)
          );

          pushEvent({
            event: 'Wishlist updated',
            data: {
              'Action Type': 'Remove',
              'Product ID': item?.sku,
              'Product Name': item?.name,
              'Product Price': item?.price ?? 0,
              'Wishlist Modification Date': new Date().toLocaleDateString(),
            },
          });

          pushGA4Event('wishlist_remove', {
            category: 'Wishlist',
            item_id: item.sku,
          });

          setWishlistToast({
            message: `"${item?.name}" removed from shelf`,
            type: 'success',
          });
          setTimeout(() => {
            setWishlistToast(undefined);
          }, 2000);
        } else {
          throw new Error('Failed to remove product from shelf');
        }
      } catch (error) {
        // If anything fails, refresh the wishlist to ensure consistency
        await getWishlistItems();
        toast.error('Failed to remove product from shelf');
        logger.error('Failed to remove product from shelf', error);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (userDetails?.token) {
      getWishlistItems();
    }
  }, [userDetails?.token]);

  const value: useWishlistPropTypes = {
    addToWishlist,
    removeFromWishlist,
    isSkuInWishlist,
    getWishlistItems,
    wishlistItems,
    isLoading,
    animateHeart,
    wishlistToast,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useWishlist = (): useWishlistPropTypes => useContext(Context);

export default WishlistProvider;
